import { ApiResponse } from "@/types/auth";
import { buildApiUrl, getAuthHeaders } from "@/config/api";

export interface RoleChangeRequest {
  id: string;
  userId: string;
  organizationId: string;
  currentRole: string;
  requestedRole: string;
  reason?: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  approvedById?: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  approvedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface CreateRoleChangeRequestParams {
  requestedRole: string;
  reason?: string;
}

export interface RoleChangeRequestsResponse {
  success: boolean;
  data: RoleChangeRequest[];
}

class RoleChangeService {
  private baseUrl = buildApiUrl("/role-changes");

  // Helper method to make API requests
  private async apiRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || "Request failed");
    }

    return response.json();
  }

  // Create a new role change request
  async createRequest(params: CreateRoleChangeRequestParams): Promise<void> {
    const response = await this.apiRequest("/request", {
      method: "POST",
      body: JSON.stringify(params),
    });

    if (!response.success) {
      throw new Error("Failed to create role change request");
    }
  }

  // Get all role change requests (admin only)
  async getRequests(): Promise<RoleChangeRequest[]> {
    const response = await this.apiRequest<RoleChangeRequestsResponse>("");

    if (!response.success || !response.data) {
      throw new Error("Failed to get role change requests");
    }

    return response.data;
  }

  // Approve a role change request (admin only)
  async approveRequest(requestId: string): Promise<void> {
    const response = await this.apiRequest(`/${requestId}/approve`, {
      method: "POST",
    });

    if (!response.success) {
      throw new Error("Failed to approve role change request");
    }
  }

  // Reject a role change request (admin only)
  async rejectRequest(requestId: string): Promise<void> {
    const response = await this.apiRequest(`/${requestId}/reject`, {
      method: "POST",
    });

    if (!response.success) {
      throw new Error("Failed to reject role change request");
    }
  }

  // Get available roles for role change requests
  getAvailableRoles(currentRole: string): Array<{ value: string; label: string }> {
    const allRoles = [
      { value: "ANALYST", label: "Analyst" },
      { value: "REVIEWER", label: "Reviewer" },
      { value: "VIEWER", label: "Viewer" },
    ];

    // Filter out current role and admin role
    return allRoles.filter(
      (role) => role.value !== currentRole && role.value !== "ADMIN"
    );
  }
}

// Export singleton instance
export const roleChangeService = new RoleChangeService();
export default roleChangeService;